import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { displayToast } from "../app/AppToast";
import Loader from "../Loader";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { getApiQueryData, useApiQuery, type Api } from "../../services/api";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import {
  canSubmitPracticeProfiles,
  resolvePracticePhase,
  toggleDisambiguation,
  effectivePracticeDisambiguation,
  inferredPracticeCount,
  practiceSearchProvenanceAfter,
  practiceSelectionDelta,
  selectedProfileUids,
  togglePracticeProfile,
  type PracticePickerPhase,
  type PracticeProfileOffer,
} from "../../domains/practice/practiceProfileSelection";
import {
  usePracticeArm,
  usePracticeDeclaration,
} from "../../domains/practice/hooks/usePracticeArm";
import { savePracticeProfiles } from "../../domains/practice/savePracticeProfiles";
import {
  gaEventPracticeProfilesConfirmed,
  gaEventPracticeQuestionShown,
} from "../../tools/analytics/domains/onboarding.analytics";
import { usePracticeSearchAssist } from "../../domains/practice/usePracticeSearchAssist";
import { PracticeProfilesPicker } from "./PracticeProfilesPicker";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
// The onboarding footer's field, reused rather than re-styled: the settings
// sheet already borrows it for the Specialty list (`MainSpecialtyView`), and two
// renderings of "search this list" is how the two screens drift apart.
import { StepSearchInput } from "../../pages/onboarding/components/StepSearchInput";
import { SearchInput } from "../ui/SearchInput";
import {
  invitableProfileUids,
  type PracticeInferenceOrigin,
} from "../../../../api/src/shared/domain/practice-profile";

// The reported source of a seeded screen, by the writer of the stored reading.
// `signup_deferred` is the reading kept when "Later" skipped the signup slide.
const PRE_SELECTION_SOURCE_BY_ORIGIN = {
  campaign: "campaign",
  signup: "signup_deferred",
} as const satisfies Record<PracticeInferenceOrigin, string>;

/** The held answer as `GET /me` serves it, and the catalogue as `/offered` does. */
type PracticeHeld =
  Api["get"]["/webapp/private/practice-profiles/me"]["output"]["body"];

/**
 * What an inference found for this reader (the campaign's, or the one kept when
 * they skipped the signup slide), and the three conditions on acting on it.
 *
 * Ticket 09 said this editor consults no evidence at all, on the grounds that
 * somebody in their own settings is deliberately editing and guessing over the
 * top of that is not a kindness. That reasoning HOLDS, and it is why
 * `seedFromInference` exists rather than this being unconditional — the settings
 * hosts still pass `false`.
 *
 * What changed is a case ticket 09 did not have: a reader who never answered, who
 * was emailed the practices we inferred for them, and who arrives here by tapping
 * "confirm". For them an empty list is not restraint, it is the link appearing to
 * have failed. So the guess is shown only where it was promised, only to somebody
 * who has said nothing yet, and only for practices this catalogue can show.
 *
 * Both halves are filtered against THIS catalogue, and the whole thing is dropped
 * when no practice survives — the same predicate the workflow branches on
 * (`practice-invite.ts`: `source === "agent" && practices.length > 0`).
 *
 * That last condition is not belt-and-braces. `readInferredProfiles` can return a
 * settled axis side with ZERO practices: the sides come from the REGISTRY when it
 * answered (rule 2), and rule 3 can then drop every recognised practice off those
 * sides. Such a reader is served `inferred` (source is `agent`, because the axis
 * was decided) and received the GENERIC email, which promises nothing pre-ticked.
 * Seeding them would skip the axis question, narrow the list to one side, and
 * pre-tick nothing — a screen that contradicts the email that sent them.
 *
 * A module function rather than the in-body IIFE it replaces: the impression
 * event reads it too, and `useTrackOnMount` cannot live under the component's
 * early returns. Pure, so the two readings cannot disagree.
 */
function seededEvidence(args: {
  readonly seedFromInference: boolean;
  readonly hasAnswered: boolean;
  readonly inferred: PracticeHeld["inferred"];
  readonly offer: PracticeProfileOffer;
  readonly offered: ReadonlySet<string>;
}): {
  readonly profileUids: readonly string[];
  readonly disambiguationValues: readonly string[];
  /** The stored reading this seed came from, for what the impression reports. */
  readonly inferred: NonNullable<PracticeHeld["inferred"]>;
} | null {
  if (!args.seedFromInference || args.hasAnswered || args.inferred === null)
    return null;
  // ⚠️ SHARED INVARIANT — see `invitableProfileUids`. The API picks the email
  // copy with this SAME function, over the same set: `/offered` serves the active
  // profiles of this catalogue Specialty, which is the query the invite action
  // runs. So a reader who was sent the "practices" copy gets a seeded picker
  // here, and one who was sent the generic copy does not. Never narrow this list
  // locally — narrow it in the shared function, where the email reads it too.
  //
  // `source` is not passed from the row: `GET /me` already refuses to serve
  // `inferred` for anything but `agent`, so by here it is the only value it can
  // be. Stated rather than re-checked, because the server's refusal is the one
  // that matters.
  const profileUids = invitableProfileUids({
    source: "agent",
    inferredProfileUids: args.inferred.profileUids,
    // The picker checks rows and does not rank them, so the order this returns is
    // not read here — but it is passed so BOTH sides compute the same capped SET.
    // Dropping it would let the two disagree about which six survive for a reader
    // carrying more than the cap.
    mainProfileUid: args.inferred.mainProfileUid,
    offeredUids: args.offered,
  });
  if (profileUids.length === 0) return null;
  // A side the catalogue no longer carries narrows the list to nothing and would
  // still count as an answer. `offer.disambiguation` is the axis as the CURRENT
  // catalogue draws it, so anything outside it is a stale token.
  const axis = args.offer.disambiguation;
  const disambiguationValues =
    axis === null
      ? []
      : args.inferred.disambiguationValues.filter((value) =>
          axis.options.some((option) => option.value === value)
        );
  return { profileUids, disambiguationValues, inferred: args.inferred };
}

/**
 * Editing the practices a practitioner holds, after signup (practice-profiles
 * ticket 08 — "my feed stays mine as my practice shifts").
 *
 * Same picker as the onboarding slide, and the same selection rules, so the two
 * surfaces cannot answer the question differently. What differs is the seed: at
 * signup nothing has been said yet and only the evidence lookup can check a row,
 * whereas here the practitioner's own saved answer is authoritative the moment
 * they have given one.
 *
 * The "at least one practice" rule holds on BOTH surfaces, which is what makes
 * it an invariant on the account rather than a rule of the signup funnel — an
 * account cannot be edited back down to zero either. It binds only in the arm,
 * so an account that predates the catalogue is still never stopped: it simply
 * has nothing to be in breach of.
 */
/**
 * Whether this visit touched the declaration. `untouched` reads the saved words
 * — the same distinction `explicit` draws for the selection — and `set` is this
 * visit's answer, an erasure included, which no `string | null` could say.
 */
type PracticeDeclarationEdit =
  | { readonly kind: "untouched" }
  | { readonly kind: "set"; readonly value: string | null };

export function PracticeProfilesEditor({
  onSaved,
  seedFromInference,
  surface,
}: {
  /**
   * What the host does once the practice is saved. Required, and never a no-op:
   * every host of this editor is something that has to get out of the way — a
   * sheet that stays open over a feed it has just changed hides the only evidence
   * the save worked, and the signup slide has a next screen to reach.
   */
  onSaved: () => void;
  /**
   * Whether a practitioner who has NEVER answered may open on the practices an
   * inference found for them.
   *
   * Required and never defaulted, because the two answers belong to different
   * surfaces and a default would silently pick one.
   *
   * `true` only on `/onboarding/practice` — the screen the invite's CTA opens,
   * where arriving to an empty list after being told the fields were pre-ticked
   * would read as the link having failed. That path is ALSO the Explore gate,
   * so a campaigned account that never opened the email meets the seeded picker
   * there too; the guess is the same one either way, which is why this is a
   * decision per SURFACE and not per arrival.
   *
   * `false` in the settings sheet and the desktop settings section: somebody
   * who opened their own settings came to state something, and meeting a guess
   * there is a different act from meeting one on a screen that exists to ask.
   */
  seedFromInference: boolean;
  /**
   * `sheet` takes the onboarding footer's field (the carousel and the mobile
   * settings sheet), `desktop` the settings modal's one.
   */
  surface: "sheet" | "desktop";
}): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const arm = usePracticeArm();
  // The same flag signup reads. Off, the sheet is exactly what it was — and the
  // round trip above still carries any words a flagged visit wrote.
  const offersDeclaration = usePracticeDeclaration();
  const specialtyUid =
    currentUser === null || currentUser.mainSpecialtyModel === null
      ? null
      : currentUser.mainSpecialtyModel.uid;

  const offerQuery = useApiQuery(
    "/webapp/public/practice-profiles/offered",
    specialtyUid === null ? false : { query: { specialtyUid } }
  );
  const heldQuery = useApiQuery("/webapp/private/practice-profiles/me", {});
  // The catalogue and the held answer BEFORE the guards below narrow them.
  // `state: "error"` hides a cached body (`createQueryResult` checks the error
  // first): a focus refetch that fails keeps the last answer on screen.
  const resolvedOffer =
    offerQuery.state === "success"
      ? offerQuery.body
      : offerQuery.state === "error" && specialtyUid !== null
        ? getApiQueryData("/webapp/public/practice-profiles/offered", {
            query: { specialtyUid },
          })
        : null;
  const resolvedHeld =
    heldQuery.state === "success"
      ? heldQuery.body
      : heldQuery.state === "error"
        ? getApiQueryData("/webapp/private/practice-profiles/me", {})
        : null;

  // The in-flight selection, through RHF rather than useState (business
  // components own no state machine). `null` is untouched — which here means
  // "show what is saved", not "show what was offered".
  // `phase` and `search` ride the same form for the same reason the selection
  // does: they are screen state, and a business component owns no state machine.
  const { watch, setValue } = useForm<{
    explicit: string[] | null;
    disambiguation: string[] | null;
    declaration: PracticeDeclarationEdit;
    phase: PracticePickerPhase | null;
    search: string;
    /** Which practices were ticked THROUGH the search field, and which of those
     * sat on a row only the assisted layer surfaced. Analytics only: how a uid
     * was reached is not part of the answer, which is the uids themselves. On
     * the form like the rest of the screen state, because a business component
     * owns no state machine. */
    viaSearch: string[];
    viaAssist: string[];
  }>({
    defaultValues: {
      explicit: null,
      disambiguation: null,
      declaration: { kind: "untouched" },
      phase: null,
      search: "",
      viaSearch: [],
      viaAssist: [],
    },
  });
  const explicit = watch("explicit");
  const disambiguation = watch("disambiguation");
  const declarationEdit = watch("declaration");
  const phase = watch("phase");
  const search = watch("search");
  const viaSearch = watch("viaSearch");
  const viaAssist = watch("viaAssist");

  // The model layer, plus the catalogue-miss report it corrects. Above the early
  // returns below, because a hook cannot live under one — it stays idle while
  // there is no Specialty and no offer.
  const practiceAssist = usePracticeSearchAssist({
    offer: resolvedOffer,
    search,
    specialtyUid,
    flow: "preference_refresh",
  });

  // Everything the seed is derived from. Up here for the same reason the hook
  // above is: the impression at the end of this block cannot live under an early
  // return, and it reports what the agent found.
  // A practitioner who has answered is the authority on their own practice, so
  // the saved set seeds the screen. Someone who never answered opens on an
  // unchecked catalogue, the same screen as at signup minus the evidence lookup.
  //
  // Read off the server's timestamp rather than the array's length, which STILL
  // matters even though the arm can no longer save an empty set: accounts that
  // predate the rule hold one, and out-of-arm accounts may still make one. The
  // two states have to stay apart, or a cleared answer would be re-seeded from
  // the offer as though it had never been given.
  const hasAnswered =
    resolvedHeld !== null && resolvedHeld.confirmedAt !== null;
  // …and only the part of that answer THIS catalogue can speak for. `GET /me`
  // returns every profile the practitioner holds, which after a change of main
  // specialty includes profiles the current Specialty does not offer: invisible
  // here (the list renders the offer, not the answer), yet carried straight back
  // into the full-replace PUT. Filtering the seed is what stops a save from
  // re-writing rows no screen can show and nobody can uncheck.
  const offered = new Set(
    resolvedOffer === null
      ? []
      : resolvedOffer.docs.map((profile) => profile.uid)
  );
  const held =
    resolvedHeld === null
      ? []
      : resolvedHeld.profileUids.filter((uid) => offered.has(uid));
  const evidence =
    resolvedOffer === null || resolvedHeld === null
      ? null
      : seededEvidence({
          seedFromInference,
          hasAnswered,
          inferred: resolvedHeld.inferred,
          offer: resolvedOffer,
          offered,
        });
  // The order seed: the saved answer, the inferred one for a reader who has
  // never answered, and nothing otherwise — never the live taps. A save re-seeds
  // it, which is right: the list has a new baseline to read back.
  const checkedOnOpen = selectedProfileUids({
    explicit: hasAnswered ? held : null,
    evidence,
  });
  // WHAT checked the screen, which `checkedOnOpen` alone cannot say: for a
  // practitioner who has answered it holds their OWN saved practices, and
  // reporting that size as the agent's reading would enter a reader correcting
  // themselves into the agent's accuracy. Only an inference counts as one.
  const baseline =
    evidence !== null
      ? {
          source: PRE_SELECTION_SOURCE_BY_ORIGIN[evidence.inferred.origin],
          // The agent's reading through the SHARED definition, never
          // `evidence.profileUids.length`: that one has been through
          // `invitableProfileUids`, which caps at `ALL_PRACTICES_MAX_ARMS`. The
          // cap decides how many rows this screen pre-ticks; it says nothing
          // about what the agent found, and counting it here made the same agent
          // report six on this flow and nine at signup.
          inferredCount: inferredPracticeCount({
            evidence: evidence.inferred,
            offer: resolvedOffer,
          }),
        }
      : hasAnswered
        ? { source: "saved_answer" as const, inferredCount: 0 }
        : { source: "none" as const, inferredCount: 0 };

  /**
   * Store which practices the search field won, after a change to the selection.
   *
   * The RULE is `practiceSearchProvenanceAfter`, shared with the signup flow:
   * it was written twice and the two copies disagreed. This closure only holds
   * the two form fields.
   */
  function writeProvenance(
    nextSelected: ReadonlySet<string>,
    reached: { uid: string; viaSearch: boolean; viaAssist: boolean } | null
  ): void {
    const next = practiceSearchProvenanceAfter({
      current: { viaSearch: new Set(viaSearch), viaAssist: new Set(viaAssist) },
      selected: nextSelected,
      reached,
    });
    setValue("viaSearch", [...next.viaSearch]);
    setValue("viaAssist", [...next.viaAssist]);
  }

  // The practice question reached an EXISTING account — the Explore gate, or the
  // settings sheet. The signup carousel emits the same event with `flow:
  // "signup"`, and that field is the only thing separating two populations with
  // no reason to answer alike.
  //
  // Distinct from `practice_profiling_gate_shown`, which fires on the page the
  // instant it mounts and therefore knows nothing: this one waits for the
  // catalogue and the seed, so it can say what the agent found.
  // Everything the impression needs, or null when there is no question on screen.
  // One value rather than a chain of guards inside the callback: the callback
  // cannot re-narrow what the target already decided, and two copies of the
  // condition are two chances for them to disagree about what "asked" means.
  //
  // An empty catalogue renders "we haven't mapped your specialty yet", which asks
  // nothing. Counting it would put a practitioner who was never given a question
  // into the population that was. The signup side draws the same line, in
  // `hasPracticeStep`.
  const questionShown =
    specialtyUid === null ||
    resolvedOffer === null ||
    resolvedHeld === null ||
    resolvedOffer.docs.length === 0
      ? null
      : { specialtyUid, catalogueSize: resolvedOffer.docs.length };

  useTrackOnMount(
    "practice-question-shown",
    questionShown === null
      ? { status: "hidden" }
      : // The FLOW is part of the claim, not just the Specialty. Both surfaces
        // emit this event and the claim store is one per browser session, so a
        // bare Specialty key lets whichever screen came first silence the other
        // — a practitioner who answered at signup and opened settings in the
        // same session would never be counted in `preference_refresh`, and that
        // flow's denominator would run short for reasons nothing in the data
        // explains.
        {
          status: "shown",
          key: `preference_refresh:${questionShown.specialtyUid}`,
        },
    () => {
      if (questionShown === null)
        throw new Error("should not happen: impression fired with no question");
      gaEventPracticeQuestionShown({
        flow: "preference_refresh",
        // This surface has no recap: the catalogue is what it opens on, always.
        screen: "picker",
        // From the closure, not the claim key, which now carries the flow too.
        specialty_uid: questionShown.specialtyUid,
        recognised_practice_count: baseline.inferredCount,
        catalogue_size: questionShown.catalogueSize,
        pre_selection_source: baseline.source,
      });
    }
  );

  if (specialtyUid === null)
    return (
      <p className="flex-1 px-5 text-ui-body text-ds-slate-500">
        {t(
          "Add a specialty to your profile and we'll show you the practices it covers."
        )}
      </p>
    );

  // A read that failed with nothing cached is a first load: nothing will come
  // back on its own, so the reader gets a retry instead of an endless loader.
  const offerFailed =
    resolvedOffer === null &&
    offerQuery.state === "error" &&
    !offerQuery.isFetching;
  const heldFailed =
    resolvedHeld === null &&
    heldQuery.state === "error" &&
    !heldQuery.isFetching;
  if (offerFailed || heldFailed)
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
        <p>{t("Something went wrong. Please try again.")}</p>
        <Button
          variant="secondary"
          onClick={() => {
            if (offerQuery.state === "error") void offerQuery.refetch();
            if (heldQuery.state === "error") void heldQuery.refetch();
          }}
        >
          {t("Retry")}
        </Button>
      </div>
    );

  if (resolvedOffer === null || resolvedHeld === null)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader />
      </div>
    );

  const offer = resolvedOffer;
  if (offer.docs.length === 0)
    return (
      <p className="flex-1 px-5 text-ui-body text-ds-slate-500">
        {t(
          "We haven't mapped the practices of your specialty yet. It's coming."
        )}
      </p>
    );

  const selected = selectedProfileUids({
    explicit: explicit === null && hasAnswered ? held : explicit,
    evidence,
  });

  // The axis answer, read back the same way the practices are: the form's if it
  // has been touched, otherwise the SAVED one.
  //
  // Served by `GET /me` rather than reconstructed here, because it is NOT
  // reconstructible: answering the question checks no practice, so a side named
  // and left unticked leaves no trace among the practices held. The reconstruction
  // this replaces lost such a side on every reopen — the question re-asked itself,
  // and the list came back unnarrowed to the whole catalogue.
  //
  // `[]` on the wire is an unanswered question, which is `null` here — the same
  // distinction `explicit`/`held` draws above between untouched and emptied.
  const savedDisambiguation = resolvedHeld.disambiguation;
  // Signup is where the words are SAID; this is where they are re-read and
  // corrected, and that second half is what lets the first one send them
  // without asking for a confirmation. The round trip stays mandatory either
  // way: the PUT replaces the field, so a visit that changed nothing must still
  // hand the saved words back rather than let Save erase them.
  const savedDeclaration = resolvedHeld.declaration;
  const effectiveDeclaration =
    declarationEdit.kind === "untouched"
      ? savedDeclaration
      : declarationEdit.value;
  // `effectivePracticeDisambiguation` owns the three-level ladder — the touched
  // answer, the saved one, then the evidence — and the signup flow reads the
  // same function for the same decision. A second ladder here would be the
  // duplicate truth that module's docstring exists to prevent.
  const effectiveDisambiguation = effectivePracticeDisambiguation({
    answer:
      disambiguation !== null
        ? disambiguation
        : savedDisambiguation.length === 0
          ? null
          : savedDisambiguation,
    evidence,
  });

  // Someone who has already answered opens on their practices, not on a question
  // they settled once — the axis stays one tap away on the recap row. Someone who
  // never answered is shown the question, because their screen is otherwise a
  // list of rows nobody ever checked for them. And a Specialty that asks nothing
  // has only the list, which is the resolver's first rule rather than a condition
  // repeated at each of this screen's four readers.
  //
  // A campaign reader counts as arriving ANSWERED when the seed carries a side.
  // Opening them on a question we hold the answer to would hide behind it the
  // pre-ticked list their email just promised.
  //
  // It is the SEED that has to carry it, not the inference: the sides and the
  // practices come from different places (`readInferredProfiles` rule 2 takes
  // the sides from the registry), so "a side exists" does NOT imply "a practice
  // was recognised". The seed above already refuses that combination, which is
  // what makes this disjunct safe.
  const currentPhase = resolvePracticePhase({
    offer,
    stored: phase,
    // The SAME function, asked without the live answer: "does this screen
    // arrive holding a side?". Reading it here rather than re-deriving keeps
    // the skip and the narrowing agreeing by construction.
    arrivedAnswered:
      hasAnswered ||
      effectivePracticeDisambiguation({ answer: null, evidence }) !== null,
  });

  // The invariant applies HERE too, and that is what makes it an invariant
  // rather than a signup rule: an account cannot be walked back down to zero
  // practices from settings either. Out of the arm — and while the arm is still
  // resolving — nothing blocks, because the requirement arrives with the
  // feature and an account that never saw the question is not in breach of it.
  //
  // The non-empty catalogue half of the condition is already spent: a Specialty
  // with no practices returned above, so there is nothing to require here.
  const blocksSave =
    arm === "in" &&
    !canSubmitPracticeProfiles({
      offer,
      disambiguation: effectiveDisambiguation,
      selected,
      // Someone who answered this question with their own words has answered
      // it, and unchecking the catalogue here does not walk that back. Dropping
      // BOTH does walk it back, and Save is then blocked, which is the one
      // thing this screen still refuses.
      declaration: effectiveDeclaration,
    });

  async function handleSave(): Promise<void> {
    const saved = await savePracticeProfiles({
      profileUids: [...selected],
      // Saved BESIDE the practices, never inferred back from them: the
      // question checks nothing, so a side named and left unticked would
      // otherwise vanish on the next open.
      disambiguation:
        effectiveDisambiguation === null ? [] : [...effectiveDisambiguation],
      declaration: effectiveDeclaration,
    });
    if (saved.status === "failed") {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    const delta = practiceSelectionDelta({
      checkedOnOpen,
      selected,
      viaSearch: new Set(viaSearch),
      viaAssist: new Set(viaAssist),
    });
    gaEventPracticeProfilesConfirmed({
      flow: "preference_refresh",
      catalogue_size: offer.docs.length,
      total_confirmed_profiles: selected.size,
      // The AGENT's reading only. A reader editing their own saved answer has
      // no inference behind this screen, and `pre_selection_source` below says
      // so rather than letting their choices pass for the agent's.
      recognised_practice_count: baseline.inferredCount,
      added_count: delta.added,
      removed_count: delta.removed,
      added_via_search_count: delta.addedViaSearch,
      added_via_assist_count: delta.addedViaAssist,
      // This surface has no recap. Stated rather than left out, so the field is
      // never absent on one of the two flows and the split stays readable.
      had_recap: false,
      // Opening the editor and pressing Save without touching anything is a
      // re-confirmation, not a change — the same distinction the signup slide
      // reports, and reporting a constant here would make the two surfaces
      // measure different things under one event name.
      changed_selection:
        explicit !== null ||
        disambiguation !== null ||
        declarationEdit.kind === "set",
      // This surface has no recap, and its Save is what submits: the words are
      // one more field the screen carries, never the control that sends it.
      confirmed_from: "picker",
      declared: effectiveDeclaration !== null,
      // What is REPORTED is the state of the answer, derived seed included —
      // a practitioner reopening the sheet on two settled species has answered
      // the question, whether or not they re-tapped it this time.
      answered_disambiguation: effectiveDisambiguation !== null,
      disambiguation_count:
        effectiveDisambiguation === null ? 0 : effectiveDisambiguation.length,
      // Which source checked the screen. `campaign` is its own bucket, not
      // `agent`: the same inference ran, but hours earlier and off a persisted
      // row rather than live at signup, and the confirmation rate of the two is
      // the comparison this campaign exists to make. `signup_deferred` is the
      // signup reading of someone who pressed "Later". `saved_answer` is the
      // case this used to report as `none` — a settings visit, where
      // the deltas below measure the practitioner against themselves.
      pre_selection_source: baseline.source,
    });
    displayToast(t("Practice updated — we'll tune your rail"), "success");
    onSaved();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <p className="m-0 shrink-0 px-5 pb-4 text-ui-body font-normal tracking-tight text-ds-slate-600">
        {t("These are the practices we show you publications for.")}
      </p>
      <div className="flex min-h-0 flex-1 flex-col px-5">
        <PracticeProfilesPicker
          offer={offer}
          selected={selected}
          checkedOnOpen={checkedOnOpen}
          disambiguation={effectiveDisambiguation}
          specialtyLabel={
            currentUser === null || currentUser.mainSpecialtyModel === null
              ? null
              : getItemTranslation(currentUser.mainSpecialtyModel)
          }
          // The Specialty is edited on its own screen here, not a slide behind,
          // so the recap row names it without offering a way back.
          onChangeSpecialty={null}
          phase={currentPhase}
          // Neither host of this editor has a title band the axis question could
          // live in: the sheet is headed "My practice" and the gate page "What
          // do you practise?", so the question itself has nowhere else to go.
          // "Check all" is kept here, unlike signup: this screen is opened on
          // purpose to re-state an answer, not to get past a question, so the
          // gesture is not an escape hatch.
          surface={{
            kind: "settings",
            declaration: offersDeclaration
              ? {
                  query: search,
                  held: effectiveDeclaration,
                  onDeclare: (words) => {
                    setValue("declaration", { kind: "set", value: words });
                    // The field has done its job, and words left in it would
                    // print twice: once on the row that now holds them, once on
                    // the offer to declare them again.
                    setValue("search", "");
                  },
                  onClear: () => {
                    setValue("declaration", { kind: "set", value: null });
                  },
                }
              : null,
          }}
          matching={practiceAssist.matching}
          isAssisting={practiceAssist.isAssisting}
          onPhaseChange={(next) => {
            setValue("phase", next);
            if (next === "axis") setValue("search", "");
          }}
          // The settings sheet and the Explore gate keep the mobile drawing on
          // every platform.
          variant="mobile"
          onToggle={(uid) => {
            const next = togglePracticeProfile(selected, uid);
            writeProvenance(new Set(next), {
              uid,
              viaSearch: search.trim() !== "",
              viaAssist: practiceAssist.assistedOnlyUids.has(uid),
            });
            setValue("explicit", next);
          }}
          onSetPractices={(uids) => {
            // A bulk set is not a field tick, so nothing is reached through it,
            // and the rows it drops lose their provenance. That is what stops
            // "Uncheck all" then "Check all" from crediting the search field for
            // a list-wide gesture — the re-added rows are genuine additions, so
            // no downstream intersection could have caught it.
            writeProvenance(new Set(uids), null);
            // An array, never `null`: null is "untouched", which on this
            // surface re-seeds the screen from the SAVED answer — so clearing
            // would redraw exactly what it just cleared.
            setValue("explicit", [...uids]);
          }}
          onToggleDisambiguation={(value) => {
            const answered = toggleDisambiguation({
              offer,
              selected,
              // The DERIVED answer, not the raw form field: starting from a
              // null `current` on the first tap after reopening would read
              // "nothing answered yet", so unchecking a side would add it
              // instead of removing it — and would leave its practices saved
              // behind a list that no longer shows them.
              current: effectiveDisambiguation,
              value,
            });
            // The axis answer only ever NARROWS the practices, so this can drop
            // rows — and a row it dropped must lose the provenance describing
            // it, or a later "Check all" credits the field for a list-wide
            // gesture. The signup flow routes its axis answer through the same
            // rule; leaving this one out is how the two surfaces drift.
            writeProvenance(new Set(answered.profileUids), null);
            setValue("disambiguation", answered.disambiguation);
            setValue("explicit", answered.profileUids);
          }}
        />
      </div>
      <div className="flex shrink-0 flex-col gap-3 px-5 pt-3">
        {/* The field sits in the bottom band, above the CTA, exactly where the
            country, profession and specialty ones sit on the carousel. Above
            the list it was the one search on this journey that scrolled with the
            rows it filters, and on a sheet it was the first thing pushed off
            screen by a thumb reaching the list.
            Hidden on the axis question: a handful of sides is nothing to search,
            and the same condition gates the carousel's own (`showPracticeSearch`). */}
        {currentPhase === "profiles" &&
          (surface === "desktop" ? (
            <SearchInput
              value={search}
              placeholder={t("Search practices")}
              // The API cap on a typed practice, as on StepSearchInput.
              maxLength={120}
              onChange={(event) => {
                setValue("search", event.target.value);
              }}
            />
          ) : (
            <StepSearchInput
              value={search}
              placeholder={t("Search practices")}
              onChange={(next) => {
                setValue("search", next);
              }}
              variant="mobile"
            />
          ))}
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          // Two jobs, because the screen has two questions and one footer. On
          // the axis question this is the way OUT of it — the taps up there
          // check species now instead of advancing, so something has to — and
          // there it is NEVER disabled, whatever the arm. Disabling the exit
          // from the first question would seal a practitioner inside it with no
          // way to reach the practices that would satisfy the rule.
          //
          // On the list it saves, and there it CAN be disabled: an in-arm
          // account may not save its way to zero practices. Nobody is trapped:
          // checking a practice enables it, and off the Explore gate the host's
          // chevron or cross leaves the saved answer exactly as it was.
          //
          // No in-flight guard: the PUT is a full replace of the same set, so a
          // double tap is idempotent, and a disabled-while-saving flag would be
          // a second state machine in a business component for no behaviour.
          disabled={currentPhase !== "axis" && blocksSave}
          onClick={() => {
            if (currentPhase === "axis") {
              setValue("phase", "profiles");
              return;
            }
            void handleSave();
          }}
        >
          {currentPhase === "axis" ? t("See my practices") : t("Save")}
        </Button>
      </div>
    </div>
  );
}
